/**
 * Shared WebSocket — ek hi connection, sab components subscribe karte hain
 */
import { getWebSocketUrl } from './wsUrl';

const RECONNECT_DELAY_MS = 3000;
const MAX_RECONNECT_ATTEMPTS = 15;
const PING_INTERVAL_MS = 25000;

let ws = null;
let currentToken = null;
let reconnectAttempts = 0;
let reconnectTimer = null;
let pingTimer = null;
let listenerId = 0;
const listeners = new Map();
const connectionStateListeners = new Set();

function notifyConnection(connected) {
  connectionStateListeners.forEach((fn) => {
    try { fn(connected); } catch (_) {}
  });
}

function routeMessage(msg) {
  const { event: eventType, data } = msg;
  for (const handlers of listeners.values()) {
    switch (eventType) {
      case 'connected':
        break;
      case 'notification':
        handlers.onNotification?.(data);
        break;
      case 'booking_update':
        handlers.onBookingUpdate?.(data);
        break;
      case 'payment_update':
        handlers.onPaymentUpdate?.(data);
        break;
      case 'chat_message':
        handlers.onChatMessage?.(data);
        break;
      case 'chat_read':
        handlers.onChatRead?.(data);
        break;
      case 'chat_typing':
        handlers.onChatTyping?.(data);
        break;
      case 'chat_updated':
        handlers.onChatUpdated?.(data);
        break;
      case 'chat_deleted':
        handlers.onChatDeleted?.(data);
        break;
      case 'chat_reaction':
        handlers.onChatReaction?.(data);
        break;
      default:
        handlers.onMessage?.(msg);
    }
  }
}

function cleanupTimers() {
  if (pingTimer) {
    clearInterval(pingTimer);
    pingTimer = null;
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function connect(token) {
  if (!token) return;

  if (ws && currentToken === token && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    return;
  }

  if (ws) {
    try { ws.close(); } catch (_) {}
    ws = null;
  }

  cleanupTimers();
  currentToken = token;
  reconnectAttempts = 0;

  try {
    ws = new WebSocket(getWebSocketUrl());

    ws.onopen = () => {
      notifyConnection(true);
      ws.send(JSON.stringify({ token }));
      reconnectAttempts = 0;
      pingTimer = setInterval(() => {
        if (ws?.readyState === WebSocket.OPEN) ws.send('ping');
      }, PING_INTERVAL_MS);
    };

    ws.onmessage = (event) => {
      const text = event.data;
      if (text === 'pong') return;
      try {
        const msg = JSON.parse(text);
        if (msg.event === 'connected') {
          notifyConnection(true);
          return;
        }
        routeMessage(msg);
      } catch (_) {}
    };

    ws.onclose = (event) => {
      notifyConnection(false);
      cleanupTimers();
      ws = null;
      if (event.code >= 4001 && event.code <= 4003) return;
      if (listeners.size === 0) return;
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return;
      const delay = RECONNECT_DELAY_MS * Math.pow(1.4, reconnectAttempts);
      reconnectAttempts += 1;
      reconnectTimer = setTimeout(() => connect(currentToken), delay);
    };

    ws.onerror = () => {};
  } catch (_) {
    notifyConnection(false);
  }
}

export function subscribeWs(handlers) {
  const id = ++listenerId;
  listeners.set(id, handlers || {});
  return () => listeners.delete(id);
}

export function subscribeWsConnection(fn) {
  connectionStateListeners.add(fn);
  fn(getWsConnected());
  return () => connectionStateListeners.delete(fn);
}

export function ensureWsConnection(token) {
  connect(token);
}

export function getWsConnected() {
  return ws?.readyState === WebSocket.OPEN;
}
